import React from "react";
import { Button, Chip, CircularProgress, Stack, Tooltip } from "@mui/material";

// FASE 05 — 2ª validação/PO 2026-07-24 (6.1): as ações agem sobre TODOS os produtos da
// empresa na situação (sem exigir seleção). "Liberar CEM Delphi/Mobi" foram substituídos
// por um único "Disponibilizar Mercado" (Homologado → Disponível Mercado; apenas MASTER).
// FASE 05 — 6ª validação/PO 2026-08-04 (Frente 2/item #1): a ação passa a valer para TODOS os
// tipos de produto (não só o selecionado).
// FASE 05 Ajustes — PO 2026-08-24 (item 6 / P1, substitui a P1 de 04/08 "sempre visíveis"):
// cada botão só aparece quando (1) Modo edição está ON e (2) há produtos na situação que o
// habilita (contagem multi-tipo do servidor). Contagem indisponível (null) mantém o botão
// visível — falha de consulta não pode esconder ação legítima.
interface BarraAcoesStatusProps {
  modoEdicaoAtivo: boolean;
  isMaster: boolean;
  // Plano 17/08/2026 (Frente 5 / decisao P9): enquanto o MOF gera os pacotes do CEM
  // Desktop, a tela precisa DIZER que esta gerando — e nao aceitar um novo disparo.
  gerandoPacotes?: boolean;
  contagemSituacoes?: {
    emEdicao: number | null;
    emHomologacao: number | null;
    homologado: number | null;
  } | null;
  onHomologar: () => void;
  onConcluirHomologacao: () => void;
  onDisponibilizarMercado: () => void;
}

export const BarraAcoesStatus: React.FC<BarraAcoesStatusProps> = ({
  modoEdicaoAtivo,
  isMaster,
  gerandoPacotes = false,
  contagemSituacoes = null,
  onHomologar,
  onConcluirHomologacao,
  onDisponibilizarMercado,
}) => {
  if (!modoEdicaoAtivo) {
    return null;
  }

  // null/undefined = contagem ainda não carregada ou falhou → mantém visível.
  const temElegiveis = (total: number | null | undefined) => total == null || total > 0;

  // Plano 30/08/2026, Frente 2 (decisão TP-25): enquanto o MOF monta os pacotes de N1, NENHUM
  // dos três botões de transição pode aparecer — só o indicador "GERANDO PACOTES…".
  //
  // Antes eles ficavam apenas desabilitados (e o CONCLUIR HOMOLOGAÇÃO nem isso: seguia
  // clicável durante a geração, permitindo disparar uma transição com os pacotes ainda sendo
  // montados). O `&& !gerandoPacotes` abaixo resolve os dois casos de uma vez, e por isso o
  // `disabled={gerandoPacotes}` deixou de ser necessário nos botões.
  const exibirHomologar = temElegiveis(contagemSituacoes?.emEdicao) && !gerandoPacotes;
  const exibirConcluir = temElegiveis(contagemSituacoes?.emHomologacao) && !gerandoPacotes;
  const exibirDisponibilizar =
    isMaster && temElegiveis(contagemSituacoes?.homologado) && !gerandoPacotes;

  if (!exibirHomologar && !exibirConcluir && !exibirDisponibilizar && !gerandoPacotes) {
    return null;
  }

  const buttonStyle = {
    textTransform: "uppercase" as const,
    fontWeight: 600,
    borderRadius: 0,
    boxShadow: "none",
    px: 3,
    py: 1.5,
    fontSize: "0.875rem",
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {gerandoPacotes && (
        <Tooltip title="O MOF está montando os pacotes de cadastros N1 para o CEM Desktop. Ao terminar, os botões voltam a ficar disponíveis.">
          <Chip
            icon={<CircularProgress size={14} thickness={5} sx={{ color: "#3C90AD" }} />}
            label="GERANDO PACOTES…"
            variant="outlined"
            sx={{ fontWeight: 600, borderColor: "#3C90AD", color: "#3C90AD" }}
          />
        </Tooltip>
      )}

      {exibirHomologar && (
        <Tooltip title="Homologar todos os produtos em Edição (todos os tipos) para validação no CEM">
          <Button
            variant="contained"
            onClick={onHomologar}
            sx={{ ...buttonStyle, backgroundColor: "#5A7A8C", "&:hover": { backgroundColor: "#4a6a7c" } }}
          >
            HOMOLOGAR
          </Button>
        </Tooltip>
      )}

      {exibirConcluir && (
        <Tooltip title="Concluir homologação de todos os produtos Em Homologação (todos os tipos)">
          <Button
            variant="contained"
            onClick={onConcluirHomologacao}
            sx={{ ...buttonStyle, backgroundColor: "#5A7A8C", "&:hover": { backgroundColor: "#4a6a7c" } }}
          >
            CONCLUIR HOMOLOGAÇÃO
          </Button>
        </Tooltip>
      )}

      {exibirDisponibilizar && (
        <Tooltip title="Disponibilizar para o mercado todos os produtos Homologados (todos os tipos; uso no CEM)">
          <Button
            variant="contained"
            onClick={onDisponibilizarMercado}
            sx={{ ...buttonStyle, backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
          >
            DISPONIBILIZAR MERCADO
          </Button>
        </Tooltip>
      )}
    </Stack>
  );
};
